import {
  Box,
  Flex,
  Heading,
  Text,
  theme,
  useMediaQuery,
} from '@everlywell/ui-kit';
import { Button } from '@everlywell/ui-kit';
import CareersBanner from 'components/CareersBanner';
import Layout from 'components/Layout';
import SectionContainer from 'components/PageSections/components/SectionContainer';
import DigitalResultsSection from 'components/PageSections/DigitalResultsSection/DigitalResultsSection';
import SplitCardSection from 'components/PageSections/SplitCardSection/SplitCardSection';
import StepCardList from 'components/StepCardList';
import { TestimonialSlider } from 'components/TestimonialSlider';
import React, { FC } from 'react';

import MediaGridCardList from '../components/MediaGridCardList/MediaGridCardList';
import DanielNewImage from '../images/careers/daniel-new@2x.webp';
import MobileAppResultsScreen from '../images/careers/mobile-app-results-screen@2x.webp';
import NicoBryantAguilarImage from '../images/careers/nico-bryant-aguilar@2x.webp';
import ReilyKotwasinskiImage from '../images/careers/reily-kotwasinski@2x.webp';
import SmileWomanLaptopColorfulFrame from '../images/careers/smiling-woman-laptop-colorful-frame@2x.webp';
import TeamMeetingLaptops from '../images/careers/team-meeting-laptops@2x.webp';
import TeamWorkingCactusMural from '../images/careers/team-working-cactus-mural@2x.webp';
import WhiteboardBrainstorming from '../images/careers/whiteboard-brainstorming@2x.webp';
import {
  createContent,
  createHeadingContent,
  createTestimonialQuote,
  createDescription,
  createImage,
  createVideo,
  createBenefit,
  createBenefitsImage,
  createStepCard,
} from '../utils/careersHelpers';
import scientistMicroscopeVideo from '../videos/careers/scientist-microscope.webm';

type SectionContext = NonNullable<
  NonNullable<Queries.pdpSectionFragment['metadata']>['context']
>;

const content = {
  title: 'Reimagine healthcare. Empower people. Grow with purpose.',
  description:
    'At Everlywell, we’re redefining how and where healthcare happens with access and precision.',
  buttonText: 'View job openings',
  bannerTitle: '',
  jobsPageUrl: 'https://jobs.lever.co/everlywell',
  stepCards: {
    eyebrow: 'Find your place here',
    title: 'Come join us',
    data: [
      {
        title: 'Make a difference',
        description:
          "Every day your talent can change someone's life in a way that's never been done before. Every small contribution plays a big part in advancing personalized healthcare.",
        desktopAlt: 'Make a difference',
        mobileAlt: 'CLIA-certified labs',
      },
      {
        title: 'Come as you are',
        description:
          'You have a place at Everlywell regardless of where you are, who you are, or what your title is. Your place here manifests in the interactions you have, the work you do, and the accomplishments you achieve.',
        desktopAlt: 'Come as you are',
        mobileAlt: 'Come as you are',
      },
      {
        title: 'Learn by doing',
        description:
          'Everyone plays a role in transforming healthcare here, which means being fearless, asking questions, learning by doing, and contributing your value every day.',
        desktopAlt: 'Learn by doing',
        mobileAlt: 'Learn by doing',
      },
    ],
  },
  coreValues: {
    eyebrow: 'Where your impact starts',
    title: 'Our core values',
    data: [
      {
        title: 'Forward together',
        description:
          'We don’t wait—we anticipate, act, and advance. By combining proactive design with true partnership, we empower people and teams to move health forward. Our strength lies in thinking ahead and working as one—across roles, disciplines, and perspectives.',
        type: 'image',
        src: TeamMeetingLaptops,
        altText: 'Forward together',
      },
      {
        title: 'Patient first',
        description:
          'Health is personal. We design every experience around people’s needs—making care simple, clear, and empowering. By putting patients first, we ensure they feel understood, supported, and in control of their health journey.',
        type: 'image',
        src: WhiteboardBrainstorming,
        altText: 'Patient first',
      },
      {
        title: 'Clinically grounded',
        description:
          'We anchor every decision in evidence, expertise, and empathy. By upholding clinical rigor and centering on real patient needs, we ensure care is safe, effective, and meaningful at every step.',
        type: 'video',
        src: scientistMicroscopeVideo,
        altText: 'Clinically grounded',
      },
      {
        title: 'Advance with AI',
        description:
          'We harness the power of AI to unlock smarter, more personalized care. By advancing technology with responsibility and purpose, we amplify human expertise and improve health at scale.',
        type: 'image',
        src: MobileAppResultsScreen,
        altText: 'Advance with AI',
      },
    ],
  },
  benefits: {
    eyebrow: 'Benefits',
    title: 'How we take care of you',
    data: [
      'Flexible time off',
      'Parental and family leave for birth and adoption',
      'Free and discounted testing for you and your family',
      'Monthly stipend for physical, financial, and emotional wellbeing',
      'Family planning stipend for fertility, adoption, surrogacy, and more',
    ].map((title, index) => ({
      id: `benefit-${index + 1}`,
      title,
    })),
  },
  testimonials: {
    data: [
      {
        quote:
          'I am inspired every day being part of a team with a singular mission of helping people live healthier, better lives through technological innovation.  Leveraging AI and advanced clinical approaches, we’re tackling complex challenges in ways that were never possible before.  The passion and expertise of the Everlywell team make it an incredible place to grow and contribute.',
        authorName: 'Daniel New',
        authorTitle: 'Principal API Architect',
        image: DanielNewImage,
      },
      {
        quote:
          'Working at Everlywell is fast-paced, collaborative, and mission-driven. We’re constantly innovating to make modern healthcare more accessible — and it’s exciting to contribute to that impact every day. I get to work with incredibly smart, high-performing teammates who are always pushing boundaries. There’s a strong culture of experimentation, especially with AI, which gives us the freedom to think big, move fast, and lead in the market.',
        authorName: 'Nico Bryant-Aguilar',
        authorTitle: 'Senior Director of Growth',
        image: NicoBryantAguilarImage,
      },
      {
        quote:
          'Working at Everlywell is both dynamic and deeply collaborative. The culture fosters curiosity, fresh ideas, and true innovation, while the people make it even more meaningful—teammates who are not only exceptionally talented but also genuinely supportive. Together, we continuously push to grow, improve, and drive change. What inspires me most is our shared commitment to serving clients and patients with excellence, knowing that the work we do creates a real and lasting impact.',
        authorName: 'Reily Kotwasinski, RN',
        authorTitle: 'Clinical Program',
        image: ReilyKotwasinskiImage,
      },
    ],
  },
  statement: {
    eyebrow: 'How we show up for each other',
    title: 'Our statement of belonging',
    description:
      'To create products that serve everyone, we must represent everyone. We’re passionate about building a community where anyone, from any background, can thrive. We believe our people are our greatest strength. Everlywell aims to create and foster a diverse community that encompasses all perspectives, building a balanced workforce where everyone feels included and welcome.',
  },
  finalCta: {
    title: 'Find your place at Everlywell',
    buttonText: 'View job openings',
    warning:
      'Please be aware of fraudulent job postings, remote work scams, and correspondence from individuals posing as a recruiter on behalf of Everlywell. All open positions are posted directly on our website.',
  },
};

const CareersPage: FC = () => {
  const [isMobile] = useMediaQuery(`(max-width: ${theme.breakpoints.tablet})`);
  return (
    <Layout isV2Page>
      {/* Hero / CTA / Button */}
      <Flex
        width="100%"
        overflowX="hidden"
        flexDirection="column"
        gap={[10, null, 10]}
        sx={{
          '& > :last-child': {
            marginBottom: ['0 !important', null, '20 !important'],
          },
        }}
      >
        <SectionContainer size={['sm', null, 'xl']}>
          <Flex
            flexDirection="column"
            alignItems="center"
            gap={[2, null, 3]}
            marginBottom={[16, null, 20]}
          >
            <Heading
              size="3xl"
              color="tints.black"
              textAlign="center"
              maxWidth={[350, 1000, 1200]}
              marginTop={[48, null, 120]}
              lineHeight={['130%', '120%']}
            >
              {content.title}
            </Heading>

            <Text
              fontSize="xl"
              color="tints.darkGrey"
              textAlign="center"
              maxWidth={[400, null, 660]}
              lineHeight={['130%', '120%', '150%']}
            >
              {content.description}
            </Text>

            <Button
              as="a"
              href={content.jobsPageUrl}
              target="_blank"
              rel="noopener noreferrer"
              variant="primary"
              id={`careers-promo`}
              width={['100%', null]}
              size="md"
              marginTop={[32]}
              maxWidth={[200, null, null]}
            >
              {content.buttonText}
            </Button>

            <CareersBanner title={content.bannerTitle} />
          </Flex>
        </SectionContainer>
        <SectionContainer padding={0}>
          <Box padding={0}>
            <StepCardList
              eyebrow={content.stepCards.eyebrow}
              title={content.stepCards.title}
              cards={content.stepCards.data.map((card) =>
                createStepCard(
                  card.title,
                  card.description,
                  card.desktopAlt,
                  card.mobileAlt,
                ),
              )}
            />
          </Box>
        </SectionContainer>
      </Flex>

      {/* Core Values Section */}
      <Box
        width="100vw"
        backgroundColor="#F9F9F6"
        marginLeft="calc(-50vw + 50%)"
        paddingTop={[40, null, 80]}
        paddingBottom="40"
      >
        <SectionContainer>
          <MediaGridCardList
            eyebrow={content.coreValues.eyebrow}
            title={content.coreValues.title}
            cards={content.coreValues.data.map((card) => ({
              title: card.title,
              description: createContent(card.description),
              ...(card.type === 'image'
                ? createImage(card.src, card.altText)
                : createVideo(card.src, card.altText)),
            }))}
          />
        </SectionContainer>
      </Box>

      {/* Benefits Section */}
      <Box paddingTop={[40, null, 120]} paddingBottom={[50, null, 120]}>
        <DigitalResultsSection
          data={{
            isContentfulSection001: true,
            contentful_id: 'benefits-section',
            displayName: 'Benefits Section',
            componentName: 'DigitalResultsSection',
            eyebrow: content.benefits.eyebrow,
            metadata: {
              title: content.benefits.title,
              metadata_id: 'benefits-section',
              context: {
                titleTag: null,
                iconName: null,
                iconColor: null,
                iconBackgroundColor: null,
                eyebrow: content.benefits.eyebrow,
                title: content.benefits.title,
                productName: null,
                seeAllLabel: null,
                seeAllIcon: null,
                seeAllUrl: null,
                symptoms: null,
                symptomsB: null,
                measuresLayout: null,
                measuresBoundary: null,
                whyMeasured: null,
                concerns: null,
                concernFilters: null,
                cardTitle: null,
                listItems: null,
                productSlug: null,
                componentName: null,
                tags: null,
                foodListTitle: null,
                comparisonLayout: null,
                emphasized: null,
                comboOnly: null,
                visibleForProductSlug: null,
                comprehensiveOnly: null,
                productOptionsLabel: null,
                productOptions: null,
                foodListInBothLabel: null,
                foodListAddedLabel: null,
                foodList: null,
                // `context` is a typed JSON blob whose key set grows every
                // time a new section is authored in Contentful, so this page
                // only sets the keys it actually uses.
              } as SectionContext,
              description: null,
              theme: null,
              ctas: null,
              flags: null,
            },
            body: {
              ...createHeadingContent(content.benefits.title, 3),
              references: [],
            },
            images: [
              createBenefitsImage(
                SmileWomanLaptopColorfulFrame,
                'Team members enjoying company benefits and wellness programs',
              ),
            ],
            subsections: [
              {
                subsections: content.benefits.data.map((benefit) =>
                  createBenefit(benefit.id, benefit.title),
                ),
              },
            ],
          }}
        />
      </Box>

      {/* Testimonials Section */}
      <Box
        sx={{
          '& .swiper': {
            '@media (max-width: 720px)': {
              'padding-bottom': '0px !important',
            },
          },
        }}
      >
        <TestimonialSlider
          swiperPaddingTop={[20, 0]}
          testimonials={content.testimonials.data.map((testimonial) => ({
            ...createTestimonialQuote(testimonial.quote),
            authorName: testimonial.authorName,
            authorTitle: testimonial.authorTitle,
            image: {
              file: {
                url: testimonial.image,
              },
              style: {
                width: isMobile ? 280 : 460,
                height: isMobile ? 280 : 460,
              },
            },
          }))}
        />
      </Box>

      {/* Statement Section */}
      <Box
        paddingTop="120px"
        paddingBottom="56px"
        paddingLeft="28px"
        paddingRight="28px"
      >
        <Box
          sx={{
            '& p': {
              '@media (max-width: 720px)': {
                'font-size': '14px !important',
                'line-height': '150% !important',
                'text-align': 'left !important',
                'padding-bottom': '2px !important',
              },
            },
            '& h5': {
              '@media (max-width: 720px)': {
                'text-align': 'left !important',
                'font-size': '20px !important',
              },
            },
            '& img': {
              '@media (max-width: 720px)': {
                'object-fit': 'cover !important',
              },
            },
          }}
        >
          <SplitCardSection
            eyebrow={content.statement.eyebrow}
            body={createHeadingContent(content.statement.title, 5)}
            description={createDescription(content.statement.description)}
            borderRadius="12px"
            mobileDirection="column"
            desktopImage={{
              gatsbyImageData: {
                layout: 'constrained',
                width: 672,
                height: 420,
                images: {
                  fallback: {
                    src: TeamWorkingCactusMural,
                  },
                },
              },
              description: content.statement.description,
            }}
            mobileImage={{
              gatsbyImageData: {
                layout: 'constrained',
                width: 358,
                height: 180,
                images: {
                  fallback: {
                    src: TeamWorkingCactusMural,
                  },
                },
              },
              description: content.statement.description,
            }}
          />
        </Box>
      </Box>

      {/* Final CTA Section */}
      <SectionContainer size={['sm', null, 'xl']} paddingBottom="16px">
        <Flex
          flexDirection="column"
          alignItems="center"
          gap={3}
          marginBottom={[16, null, 20]}
        >
          <Heading
            size="3xl"
            color="tints.black"
            textAlign="center"
            maxWidth={1000}
            marginTop="40"
          >
            {content.finalCta.title}
          </Heading>
          <Button
            as="a"
            href={content.jobsPageUrl}
            target="_blank"
            rel="noopener noreferrer"
            variant="primary"
            id={`careers-promo`}
            width={['100%', null]}
            size="md"
            marginTop={[2]}
            marginBottom={[-2]}
            maxWidth={[200, null, null]}
          >
            {content.finalCta.buttonText}
          </Button>
          <Text
            size="md"
            marginTop="24"
            color="tints.darkGrey"
            textAlign="center"
            maxWidth={670}
            padding="0 28px"
          >
            {content.finalCta.warning}
          </Text>
        </Flex>
      </SectionContainer>
    </Layout>
  );
};

export const Head = () => <title>Everlywell | Careers</title>;

export default CareersPage;
